// Modal de pré-visualização/envio de anexos do atendimento (abre pelo "+" do
// MessageInput ao escolher imagem/vídeo/documento, pela câmera e pelo
// arrastar-e-soltar), no padrão Aeros Dark: casca AerosDialog + estilos
// compartilhados de styles/aerosModalStyles. A lógica (legenda por arquivo,
// navegação entre eles, atalhos de teclado) é a mesma da versão anterior.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, TextField } from '@material-ui/core';
import { NavigateBefore, NavigateNext, Send } from '@material-ui/icons';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import { makeStyles, alpha } from "@material-ui/core/styles";

import AudioModal from '../AudioModal';
import AerosDialog from '../AerosDialog';
import useAerosModalStyles from '../../styles/aerosModalStyles';
import { AEROS, stateText } from '../../styles/aerosTokens';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => {
    const isLight = theme.palette.type === "light";
    const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
    return {
        stage: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 380,
            maxHeight: '46vh',
            borderRadius: 12,
            border: `1px solid ${border}`,
            backgroundColor: isLight ? "rgba(15,20,40,0.03)" : "rgba(0,0,0,0.28)",
            overflow: 'hidden',
            position: 'relative',
            padding: 8,
        },
        media: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 },
        fallback: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            color: theme.palette.text.secondary,
            fontSize: 12.5,
            "& .MuiSvgIcon-root": {
                fontSize: 34,
                color: stateText(theme, AEROS.cyan, "info"),
            },
        },
        fileRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
        },
        fileName: {
            fontSize: 12.5,
            fontWeight: 600,
            color: theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        counter: {
            fontSize: 11.5,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            minWidth: 44,
            textAlign: 'center',
        },
        navButton: {
            padding: 4,
            color: theme.palette.text.secondary,
            backgroundColor: isLight ? "rgba(15,20,40,0.04)" : "rgba(255,255,255,0.05)",
            "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.15) },
            "&.Mui-disabled": { opacity: 0.4 },
        },
    };
});

const MessageUploadMedias = ({ isOpen, files, onClose, onSend, onCancelSelection }) => {
    const m = useAerosModalStyles();
    const classes = useStyles();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [captions, setCaptions] = useState(files.map(() => ''));
    const [componentMounted, setComponentMounted] = useState(false);
    const [firstTyping, setFirstTyping] = useState(false);

    useEffect(() => {
        setFirstTyping(true);
        setComponentMounted(true);
    }, []);

    const handleClose = () => {
        onClose();
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setFirstTyping(true);
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentIndex < files.length - 1) {
            setFirstTyping(true);
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handleCaptionChange = (e) => {
        const value = e.target.value;
        setCaptions((prevCaptions) => {
            const updatedCaptions = [...prevCaptions];
            updatedCaptions[currentIndex] = value;
            return updatedCaptions;
        });
    };

    const handleSend = () => {
        const selectedMedias = files.map((file, index) => ({
            file,
            caption: captions[index],
        }));
        onSend(selectedMedias);
        handleClose();
    };

    const currentFile = files[currentIndex];

    const renderFileContent = useMemo(() => {
        if (!componentMounted) {
            return null;
        }
        if (firstTyping) {
            const currentFile = files[currentIndex];

            if (currentFile.type.startsWith('image')) {
                return (
                    <img
                        alt={i18n.t("aeros.mediaUpload.imageAlt", { index: currentIndex + 1 })}
                        src={URL.createObjectURL(currentFile)}
                        className={classes.media}
                    />
                );
            } else if (currentFile.type.startsWith('video')) {
                return (
                    <video
                        src={URL.createObjectURL(currentFile)}
                        controls
                        volume={localStorage.getItem("volume")}
                        className={classes.media}
                    />
                );
            } else if (currentFile.type.startsWith('audio')) {
                return <AudioModal url={URL.createObjectURL(currentFile)} />;
            }
            // PDF e outros tipos sem visualização específica
            return (
                <div className={classes.fallback}>
                    <InsertDriveFileOutlinedIcon />
                    <span>{i18n.t("aeros.mediaUpload.noPreview")}</span>
                    <span>{currentFile.type || i18n.t("aeros.mediaUpload.unknownType")}</span>
                </div>
            );
        }
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, firstTyping, componentMounted]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && event.shiftKey) {
            return;
        }
        switch (event.key) {
            case 'Escape':
                onCancelSelection();
                break;
            case 'Enter':
                handleSend();
                break;
            case 'ArrowRight':
                handleNext();
                break;
            case 'ArrowLeft':
                handlePrev();
                break;
            default:
                break;
        }
    };

    return (
        <AerosDialog
            open={isOpen}
            onClose={onCancelSelection}
            width={760}
            title={i18n.t("aeros.mediaUpload.title")}
            subtitle={
                files.length > 1
                    ? i18n.t("aeros.mediaUpload.subtitleMany", { total: files.length })
                    : i18n.t("aeros.mediaUpload.subtitleOne")
            }
            onSubmit={handleSend}
            submitLabel={i18n.t("aeros.mediaUpload.send")}
            submitIcon={<Send />}
            footerLeft={
                files.length > 1 ? (
                    <Box display="flex" alignItems="center" gridGap={4}>
                        <IconButton
                            onClick={handlePrev}
                            disabled={currentIndex === 0}
                            className={classes.navButton}
                            aria-label={i18n.t("aeros.mediaUpload.previous")}
                        >
                            <NavigateBefore />
                        </IconButton>
                        <span className={classes.counter}>
                            {currentIndex + 1} / {files.length}
                        </span>
                        <IconButton
                            onClick={handleNext}
                            disabled={currentIndex === files.length - 1}
                            className={classes.navButton}
                            aria-label={i18n.t("aeros.mediaUpload.next")}
                        >
                            <NavigateNext />
                        </IconButton>
                    </Box>
                ) : undefined
            }
        >
            <div className={classes.stage}>{renderFileContent}</div>

            <div className={classes.fileRow}>
                <span className={classes.fileName}>{currentFile?.name}</span>
            </div>

            <div>
                <label className={m.fieldLabel}>{i18n.t("aeros.mediaUpload.captionLabel")}</label>
                <TextField
                    className={m.input}
                    variant="outlined"
                    placeholder={i18n.t("aeros.mediaUpload.captionPlaceholder")}
                    multiline
                    rows={2}
                    rowsMax={4}
                    value={captions[currentIndex]}
                    onChange={handleCaptionChange}
                    autoFocus
                    onKeyDown={handleKeyDown}
                />
                <div className={m.hint}>{i18n.t("aeros.mediaUpload.captionHint")}</div>
            </div>
        </AerosDialog>
    );
};

export default MessageUploadMedias;
